/**
 * Header.tsx — topbar above the page content.
 *
 * Shows breadcrumb (section / page) on the left and the signed-in user's
 * name + a logout shortcut on the right. The breadcrumb pulls from
 * `PAGE_TITLES` so changing menu labels in `menu.ts` is the single source
 * of truth.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Ico from '../components/Ico';
import { PAGE_TITLES } from '../menu';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logoutThunk, selectUser } from '../redux/slices/auth';

function pageKeyFromPath(pathname: string): string {
	if (pathname === '/' || pathname === '') return 'overview';
	if (pathname.startsWith('/banks/submission-status')) return 'bank-submission-status';
	if (/^\/banks\/[^/]+\/branches\//.test(pathname)) return 'branch-detail';
	if (pathname.startsWith('/banks/')) return 'bank-detail';
	if (/^\/products\/.+/.test(pathname)) return 'product-detail';
	const head = pathname.split('/').filter(Boolean)[0] || '';
	return head in PAGE_TITLES ? head : 'overview';
}

export default function Header() {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const user = useAppSelector(selectUser);

	const key = pageKeyFromPath(location.pathname);
	const [section, page] = PAGE_TITLES[key] || ['UCB Dashboard', ''];

	const handleLogout = async () => {
		await dispatch(logoutThunk());
		navigate('/login', { replace: true });
	};

	return (
		<div className="topbar">
			<div className="crumbs">
				{section} <span style={{ margin: '0 6px' }}>/</span> <b>{page}</b>
			</div>
			<div style={{ flex: 1 }} />
			<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
				{user && (
					<span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
						Signed in as <b style={{ color: 'var(--text)' }}>{user.fullName || user.username}</b>
					</span>
				)}
				<button
					type="button"
					className="icon-btn"
					onClick={handleLogout}
					title="Logout"
					aria-label="Logout"
				>
					<Ico name="logout" size={14} />
				</button>
			</div>
		</div>
	);
}
